import React, { useState } from "react";

const getColorByPercentage = (percentage) => {
  if (percentage === null || percentage === undefined) return "secondary";
  if (percentage <= 33) return "success";
  if (percentage <= 66) return "warning";
  if (percentage <= 95) return "warning"; // Using warning for high utilization (orange-ish)
  return "danger";
};

const getColorLabel = (percentage) => {
  if (percentage <= 33) return "Low";
  if (percentage <= 66) return "Medium";
  if (percentage <= 95) return "High";
  return "Critical";
};

const EmployeeListModal = ({ employees, title, onClose }) => {
  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc, true); // Use capture phase
    return () => document.removeEventListener('keydown', handleEsc, true);
  }, [onClose]);

  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!employees || employees.length === 0) return null;

  return (
    <div className="modal modal-blur fade show" style={{ display: "block", zIndex: 1060 }} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-users me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" /><path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /><path d="M21 21v-2a4 4 0 0 0 -3 -3.85" /></svg>
              {title} - {employees.length} Employee{employees.length !== 1 ? 's' : ''}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="table-responsive">
              <table className="table table-sm table-vcenter table-striped">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Job Family</th>
                    <th>Supervisor</th>
                    <th>Expected Zone</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp, idx) => (
                    <tr key={idx}>
                      <td><small className="text-muted">{idx + 1}</small></td>
                      <td><small><strong>{emp.fullName || 'N/A'}</strong></small></td>
                      <td><small>{emp.jobFamily || 'N/A'}</small></td>
                      <td><small>{emp.supervisorFullName || 'N/A'}</small></td>
                      <td><small>{emp.expectedZone || 'N/A'}</small></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const JobFamilyGroupView = ({ jobFamilyGroups }) => {
  const [showEmployees, setShowEmployees] = useState(null);

  return (
    <>
      <div className="table-responsive">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Job Family Group</th>
              <th className="text-end">Count</th>
              <th className="text-end">Utilization</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobFamilyGroups.map((group, idx) => (
              <tr key={idx}>
                <td>{group.jobFamilyGroup}</td>
                <td className="text-end">{group.count}</td>
                <td className="text-end">
                  <span className="me-2">{typeof group.percentage === 'number' ? group.percentage.toFixed(1) : group.percentage}%</span>
                  <span className={`badge bg-${getColorByPercentage(group.percentage)}`}>
                    {getColorLabel(group.percentage)}
                  </span>
                </td>
                <td className="text-end">
                  {group.employees && group.employees.length > 0 ? (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEmployees(group);
                      }}
                      title="View employee list"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-users me-1" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" /><path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /><path d="M21 21v-2a4 4 0 0 0 -3 -3.85" /></svg>
                      {group.employees.length}
                    </button>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showEmployees && (
        <>
          <EmployeeListModal
            employees={showEmployees.employees}
            title={showEmployees.jobFamilyGroup}
            onClose={() => setShowEmployees(null)}
          />
          <div className="modal-backdrop fade show" style={{ zIndex: 1055 }}></div>
        </>
      )}
    </>
  );
};

const JobFamilyView = ({ jobFamilies }) => {
  const [showEmployees, setShowEmployees] = useState(null);

  return (
    <>
      <div className="table-responsive">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Job Family</th>
              <th className="text-end">Count</th>
              <th className="text-end">Utilization</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobFamilies.map((family, idx) => (
              <tr key={idx}>
                <td>{family.jobFamily}</td>
                <td className="text-end">{family.count}</td>
                <td className="text-end">
                  <span className="me-2">{typeof family.percentage === 'number' ? family.percentage.toFixed(1) : family.percentage}%</span>
                  <span className={`badge bg-${getColorByPercentage(family.percentage)}`}>
                    {getColorLabel(family.percentage)}
                  </span>
                </td>
                <td className="text-end">
                  {family.employees && family.employees.length > 0 ? (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEmployees(family);
                      }}
                      title="View employee list"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-users me-1" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" /><path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /><path d="M21 21v-2a4 4 0 0 0 -3 -3.85" /></svg>
                      {family.employees.length}
                    </button>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showEmployees && (
        <>
          <EmployeeListModal
            employees={showEmployees.employees}
            title={showEmployees.jobFamily}
            onClose={() => setShowEmployees(null)}
          />
          <div className="modal-backdrop fade show" style={{ zIndex: 1055 }}></div>
        </>
      )}
    </>
  );
};

export const ZoneBreakdownModal = ({ entry, floorCapacity, onClose }) => {
  const [viewMode, setViewMode] = useState("jobFamilyGroup");
  const [expandedZones, setExpandedZones] = useState([]);

  // Auto-expand all zones on mount - MUST be before any returns
  React.useEffect(() => {
    if (entry && entry.zones) {
      setExpandedZones(entry.zones.map((_, idx) => idx));
    }
  }, [entry]);

  // ESC key handler
  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!entry || !entry.zones) return null;

  const dateTime = `${entry.hour % 12 || 12}:00 ${entry.hour >= 12 ? "PM" : "AM"}`;

  const toggleZone = (index) => {
    if (expandedZones.includes(index)) {
      setExpandedZones(expandedZones.filter((i) => i !== index));
    } else {
      setExpandedZones([...expandedZones, index]);
    }
  };

  return (
    <div className="modal modal-blur fade show" style={{ display: "block" }} tabIndex="-1">
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-building me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 21l18 0" /><path d="M9 8l1 0" /><path d="M9 12l1 0" /><path d="M9 16l1 0" /><path d="M14 8l1 0" /><path d="M14 12l1 0" /><path d="M14 16l1 0" /><path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16" /></svg>
              Zone Breakdown - {dateTime} ({entry.count} / {floorCapacity})
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-header bg-light border-bottom">
            <span className="text-muted">
              ({entry.zones?.length || 0}) Zone{entry.zones?.length !== 1 ? 's are' : ' is'} used
            </span>
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                className={`btn ${viewMode === "jobFamilyGroup" ? "btn-danger" : "btn-outline-secondary"}`}
                onClick={() => setViewMode("jobFamilyGroup")}
              >
                Job Family Groups
              </button>
              <button
                type="button"
                className={`btn ${viewMode === "jobFamily" ? "btn-danger" : "btn-outline-secondary"}`}
                onClick={() => setViewMode("jobFamily")}
              >
                Job Families
              </button>
            </div>
          </div>

          <div className="modal-body" style={{ maxHeight: "70vh" }}>
            {entry.zones?.length === 0 ? (
              <div className="text-center py-5">
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-building text-muted mb-3" width="48" height="48" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 21l18 0" /><path d="M9 8l1 0" /><path d="M9 12l1 0" /><path d="M9 16l1 0" /><path d="M14 8l1 0" /><path d="M14 12l1 0" /><path d="M14 16l1 0" /><path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16" /></svg>
                <p className="text-muted">No zones found</p>
              </div>
            ) : (
              <div className="accordion" id="zonesAccordion">
                {entry.zones.map((zone, index) => (
                  <div className="card mb-2" key={index}>
                    <div
                      className="card-header d-flex justify-content-between align-items-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleZone(index)}
                    >
                      <div className="d-flex align-items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-building text-danger me-2" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 21l18 0" /><path d="M9 8l1 0" /><path d="M9 12l1 0" /><path d="M9 16l1 0" /><path d="M14 8l1 0" /><path d="M14 12l1 0" /><path d="M14 16l1 0" /><path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16" /></svg>
                        <div>
                          <strong>{zone.zoneName}</strong>
                          <div className="small text-muted">
                            {zone.count} of {zone.capacity} seats filled
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span className={`badge bg-${getColorByPercentage(zone.percentage)}`}>
                          {zone.percentage?.toFixed(0)}%
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="icon"
                          width="20"
                          height="20"
                          style={{
                            transform: expandedZones.includes(index) ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s"
                          }}
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </div>
                    </div>
                    {expandedZones.includes(index) && (
                      <div className="card-body bg-light">
                        {viewMode === "jobFamilyGroup" ? (
                          zone.jobFamilyGroups && zone.jobFamilyGroups.length > 0 ? (
                            <JobFamilyGroupView jobFamilyGroups={zone.jobFamilyGroups} />
                          ) : (
                            <div className="text-center text-muted py-3">
                              No job family groups in this zone
                            </div>
                          )
                        ) : (
                          zone.jobFamilies && zone.jobFamilies.length > 0 ? (
                            <JobFamilyView jobFamilies={zone.jobFamilies} />
                          ) : (
                            <div className="text-center text-muted py-3">
                              No job families in this zone
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-danger" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
